'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import { useLazyUserLogoutQuery } from '@/src/api/rtkQuery/userApi'

import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser, clearAuthUser } from '@/src/redux/slices/authSlice'
import { deleteCookie } from '@/src/utils/browser'
import { SignInModalWrapper } from '../flyinHeader/SignInModal'

import { MobileHeader } from './mobileHeader'

interface MobileHeaderContainerProps {
    hideSignIn?: boolean
}

const MobileHeaderContainer: React.FC<MobileHeaderContainerProps> = ({ hideSignIn = false }) => {
    const [isMounted, setIsMounted] = useState(false)
    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)

    const dispatch = useAppDispatch()
    const user = useAppSelector(selectAuth)
    const t = useTranslations('header')
    const [triggerLogout] = useLazyUserLogoutQuery()

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        userMenuAnchorRef.current = event.currentTarget
        setMenuOpen(true)
    }, [])

    const handleMenuClose = useCallback(() => {
        userMenuAnchorRef.current = null
        setMenuOpen(false)
    }, [])

    const handleModalOpen = useCallback(() => {
        setModals(prev => ({ ...prev, signIn: true }))
    }, [])

    const handleModalClose = useCallback(() => {
        setModals(prev => ({ ...prev, signIn: false }))
    }, [])

    const handleSignOut = useCallback(async () => {
        try {
            await triggerLogout()?.unwrap()
            dispatch(clearAuthUser())
            const domain = '.' + window.location.hostname
            deleteCookie('usermisc', domain)
            deleteCookie('userid', domain)
            deleteCookie('userData', domain)
            dispatch(refreshAuthUser())
            handleMenuClose()
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }, [dispatch, triggerLogout, handleMenuClose])

    // Prevent hydration mismatch: use default user state until mounted
    // This ensures server and initial client render match
    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <MobileHeader
                user={displayUser}
                menuAnchor={userMenuAnchorRef.current}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onModalOpen={handleModalOpen}
                onSignOut={handleSignOut}
                t={t}
                disableUserMenu={hideSignIn}
            />
            <SignInModalWrapper open={modals.signIn} onClose={handleModalClose} onMenuClose={handleMenuClose} />
        </>
    )
}

export { MobileHeaderContainer as MobileHeader }
export default MobileHeaderContainer
